/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

/**
 * Response Source Indicator
 * Phase 2B: Shows users how their response was generated
 *
 * Small icon that appears next to SkillBot messages to show
 * whether response came from specialized adapters or fallback.
 */

import React, { useState, useCallback } from 'react';
import api from '../services/api';
import './ResponseSourceIndicator.css';

const ResponseSourceIndicator = ({ messageId, transparency }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sourceInfo, setSourceInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // All hooks must be called before any conditional returns
  const loadSourceInfo = useCallback(async () => {
    if (sourceInfo || loading) return; // Already loaded or loading

    setLoading(true);
    try {
      const response = await api.get(`/response-source/${messageId}`);
      if (response.data?.success) {
        setSourceInfo(response.data.data);
      } else {
        setSourceInfo({ type: 'unknown', explanation: 'Source information unavailable' });
      }
    } catch (error) {
      console.error('Failed to load source info:', error);
      setSourceInfo({ type: 'unknown', explanation: 'Source information unavailable' });
    }
    setLoading(false);
  }, [messageId, sourceInfo, loading]);

  const handleClick = useCallback(() => {
    loadSourceInfo();
    setShowModal(true);
  }, [loadSourceInfo]);

  const handleMouseEnter = useCallback(() => {
    if (transparency?.showTooltip) {
      setShowTooltip(true);
    }
  }, [transparency?.showTooltip]);

  // Don't render if transparency is disabled (after all hooks)
  if (!transparency?.showSource) return null;

  const getSourceIcon = (type) => {
    const icons = {
      adapter_stack: '🧠',
      claude: '🤖',
      openai: '🔮',
      groq: '⚡',
      error: '⚠️',
      unknown: 'ℹ️'
    };
    return icons[type] || 'ℹ️';
  };

  const getSourceLabel = (type) => {
    const labels = {
      adapter_stack: 'Specialized AI',
      claude: 'Claude AI',
      openai: 'OpenAI GPT',
      groq: 'Groq LLaMA',
      error: 'Fallback',
      unknown: 'Unknown'
    };
    return labels[type] || 'Unknown';
  };

  const getSourceColorClass = (type) => {
    const colors = {
      adapter_stack: 'source-green',
      claude: 'source-blue',
      openai: 'source-purple',
      groq: 'source-orange',
      error: 'source-red',
      unknown: 'source-gray'
    };
    return colors[type] || 'source-gray';
  };

  return (
    <>
      {/* Icon Button */}
      <button
        className={`response-source-btn ${sourceInfo ? getSourceColorClass(sourceInfo.type) : 'source-gray'}`}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
        title={transparency?.tooltipMessage || 'Click to see how this response was generated'}
      >
        {loading ? (
          <span className="source-loading">...</span>
        ) : (
          <span className="source-icon">{sourceInfo ? getSourceIcon(sourceInfo.type) : 'ℹ️'}</span>
        )}

        {/* Tooltip */}
        {showTooltip && !showModal && (
          <div className="source-tooltip">
            {transparency?.tooltipMessage || 'Want to know how this response was generated?'}
            <div className="tooltip-arrow" />
          </div>
        )}
      </button>

      {/* Modal */}
      {showModal && (
        <div className="source-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="source-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className={`source-modal-header ${sourceInfo ? getSourceColorClass(sourceInfo.type) : ''}`}>
              <div className="header-content">
                <span className="header-icon">
                  {sourceInfo ? getSourceIcon(sourceInfo.type) : '🔍'}
                </span>
                <div className="header-text">
                  <h3>Response Source</h3>
                  <p>{sourceInfo ? getSourceLabel(sourceInfo.type) : 'Loading...'}</p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            {/* Body */}
            <div className="source-modal-body">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner" />
                  <p>Loading source information...</p>
                </div>
              ) : sourceInfo ? (
                <>
                  <p className="source-explanation">{sourceInfo.explanation}</p>

                  {/* Adapters Used */}
                  {sourceInfo.adapters && sourceInfo.adapters.length > 0 && (
                    <div className="source-detail">
                      <span className="detail-label">Adapters Used</span>
                      <div className="adapter-tags">
                        {sourceInfo.adapters.map((adapter, idx) => (
                          <span key={idx} className="adapter-tag">{adapter}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Response Time */}
                  {sourceInfo.responseTime && (
                    <div className="source-detail">
                      <span className="detail-label">Response Time</span>
                      <span className="detail-value">{sourceInfo.responseTime}ms</span>
                    </div>
                  )}

                  {/* Fallback Info */}
                  {sourceInfo.usedFallback && (
                    <div className="fallback-notice">
                      <span className="notice-icon">ℹ️</span>
                      <span>This response used a fallback model</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="no-info">Source information is not available for this message.</p>
              )}
            </div>

            {/* Footer */}
            <div className="source-modal-footer">
              <button className="btn-got-it" onClick={() => setShowModal(false)}>
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResponseSourceIndicator;
